import type { Router, RouteRecordNormalized, RouteLocationNormalizedLoaded } from 'vue-router'
import { getRouteTree } from '@/router/routeTree'
import { getRouteNavLabel, getRoutePathString, getSearchableRouteRecords } from '@/router/routeCatalog'
// @ts-expect-error importing untyped JS module into TypeScript
import { hasAccessToRouteName } from '@/router/RouterAccessGuard'
import { matchSearchableRoutes } from '@/utils/routeSearch'
import { getActiveSection } from './activeSection'
import { FALLBACK_NAV_ICON, isNavIconName } from './navIcons'
import { DEFAULT_NAV_COLOR, isNavColor, type NavColor } from './navColors'

/**
 * Everything that can be placed in the sidebar, with the defaults (name, icon, color,
 * description) it gets when added.
 *
 * Two kinds of entry:
 * - Section hubs: the landing page of a whole area (Attendance, Payroll...). These are the
 *   console's original sidebar items. Their parent routes are not pages in the router's sense --
 *   opening one redirects to a tab -- so they are described here by hand.
 * - Pages: every route the header search lists, read from the router. Their defaults come from
 *   route meta (`navLabel`, `navIcon`, `navColor`, `description`), so a new page shows up here
 *   by being added to the router, with no change to this file.
 *
 * Every item defaults to a plain icon with no colored tile (`transparent`), matching the console's
 * original sidebar; a color is something a person chooses.
 *
 * Entry ids are what a person's stored layout refers to, so a hub id must never change. Page
 * entries use the route name as id; renaming a route orphans links to it (they stop rendering,
 * they do not break anything).
 */

export interface NavCatalogEntry {
  id: string
  kind: 'hub' | 'page'
  label: string
  description?: string
  /** Where the page sits, e.g. "Attendance / Time Cards / Editor". Helps tell similar names apart. */
  trail: string
  icon: string
  color: NavColor
  category: string
  /** Report pages only: the report category (Attendance, Payroll...) they are filed under. */
  subcategory?: string
  searchAliases: string[]
  /** Candidate link targets, in order. The first the person may open is used. */
  routeNames: string[]
  tourId?: string
  /** Root state key holding a count to show as a badge on the item. */
  badgeStateKey?: 'activeUsers' | 'activeDevices'
  /**
   * Always at the top of everyone's sidebar, outside the part they customize, so it is never
   * offered in the picker and a stored link to it is not drawn a second time.
   */
  fixed?: boolean
}

interface HubDefinition {
  id: string
  label: string
  description: string
  icon: string
  routeNames: string[]
  /** The route the whole area is nested under, used to tell when the hub is the active item. */
  rootRouteName: string
  /** Former names, as for a route's `searchAliases`, so the picker's search still finds the hub by them. */
  searchAliases?: string[]
  badgeStateKey?: NavCatalogEntry['badgeStateKey']
  fixed?: boolean
}

const HUBS: HubDefinition[] = [
  // The start page: the console lands here, so the way back to it is never customized away.
  { id: 'overview', label: 'Overview', description: 'Key metrics for your organization at a glance.', icon: 'LayoutDashboard', routeNames: ['dashboard'], rootRouteName: 'dashboard', searchAliases: ['Dashboard'], fixed: true },
  { id: 'attendance', label: 'Attendance', description: 'Time cards, punches, clock logs and attendance infractions.', icon: 'Clock', routeNames: ['attendance-index'], rootRouteName: 'attendance' },
  { id: 'schedule', label: 'Schedule', description: 'Build shift schedules and manage teams.', icon: 'Calendar', routeNames: ['schedule-index'], rootRouteName: 'schedule' },
  { id: 'devices', label: 'Devices', description: 'Time clock devices, geofences and clock-in settings.', icon: 'Cpu', routeNames: ['device-list'], rootRouteName: 'devices', badgeStateKey: 'activeDevices' },
  { id: 'users', label: 'Users', description: 'Workers, admins and their profiles.', icon: 'Users', routeNames: ['employee-list'], rootRouteName: 'users', badgeStateKey: 'activeUsers' },
  // Admins with settings-only access to time off and payroll still get the hub, landing on settings.
  { id: 'timeoff', label: 'Time Off', description: 'Time off entries, requests, holidays and policies.', icon: 'Calendar', routeNames: ['timeoff-index', 'settings-timeoff-redirect'], rootRouteName: 'timeoff' },
  { id: 'forms', label: 'Forms', description: 'Build forms and review submissions.', icon: 'Clipboard', routeNames: ['forms-list'], rootRouteName: 'forms' },
  { id: 'costing', label: 'Job Costing', description: 'Jobs, phases and cost codes.', icon: 'Coins', routeNames: ['costing-index'], rootRouteName: 'costing' },
  { id: 'payroll', label: 'Payroll', description: 'Pay runs, adjustments and payroll settings.', icon: 'CreditCard', routeNames: ['payroll-index', 'settings-payroll-redirect'], rootRouteName: 'payroll' },
  { id: 'systemops', label: 'System Ops', description: 'Message delivery activity and diagnostics.', icon: 'Wrench', routeNames: ['system-ops'], rootRouteName: 'system-ops' },
  { id: 'reports', label: 'Reports Center', description: 'Browse and run every report.', icon: 'ChartColumn', routeNames: ['reports'], rootRouteName: 'reports' }
]

const HUB_BY_ID = new Map(HUBS.map(hub => [hub.id, hub]))
const HUB_BY_ROOT_ROUTE = new Map(HUBS.map(hub => [hub.rootRouteName, hub]))

/**
 * Pages the router lists that are not offered on their own: hub landing pages (the hub covers
 * them), the fixed footer links (Settings, Byxbi), and pages that only make sense opened from a
 * specific record (a particular form).
 */
const EXCLUDED_ROUTE_NAMES = new Set([
  'dashboard', 'forms', 'forms-list', 'schedule', 'payroll', 'timeoff', 'system-ops', 'costing', 'reports',
  'device-list', 'employee-list',
  'settings', 'byxbi',
  'form-builder', 'form-submission', 'forms-view'
])

/** Only pages inside an organization can be placed in the sidebar. */
const ORG_ROOT_ROUTE_NAME = 'org-namespace'

export interface NavCategory {
  id: string
  label: string
}

export const RECOMMENDED_CATEGORY_ID = 'recommended'
export const ALL_CATEGORY_ID = 'all'

/** The picker's left rail, in order. Empty categories are hidden by the picker. */
export const NAV_CATEGORIES: NavCategory[] = [
  { id: RECOMMENDED_CATEGORY_ID, label: 'Recommended' },
  { id: ALL_CATEGORY_ID, label: 'All' },
  ...HUBS.filter(hub => hub.id !== 'overview' && hub.id !== 'reports').map(hub => ({ id: hub.id, label: hub.label })),
  { id: 'reports', label: 'Reports' },
  { id: 'settings', label: 'Settings' },
  { id: 'general', label: 'General' }
]

const CATEGORY_ICONS: Record<string, string> = {
  ...Object.fromEntries(HUBS.map(hub => [hub.id, hub.icon])),
  settings: 'Settings',
  general: FALLBACK_NAV_ICON
}

/**
 * What the picker suggests: pages admins commonly want one click away that the standard sidebar
 * does not already have, most broadly useful first. Hubs are deliberately absent -- they are all
 * in the standard sidebar, and one someone removed is easy to find again under its own category.
 * Entries the person cannot open, or already has, are skipped.
 */
export const RECOMMENDED_ENTRY_IDS = [
  'timecard-editor', 'missed-punch-list', 'timeoff-requests', 'shift-extend-requests', 'schedules',
  'roll-call', 'timecards-report', 'total-report', 'memorized', 'pay-run-list', 'punch-list',
  'clock-log-list', 'geomap'
]

/** Hub ids carry their own tour anchor (`nav-attendance`, ...), matching the original sidebar. */
const hubEntry = (hub: HubDefinition): NavCatalogEntry => ({
  id: hub.id,
  kind: 'hub',
  label: hub.label,
  description: hub.description,
  trail: hub.label,
  icon: hub.icon,
  color: DEFAULT_NAV_COLOR,
  category: hub.id === 'overview' ? 'general' : hub.id,
  searchAliases: hub.searchAliases ?? [],
  routeNames: hub.routeNames,
  tourId: hub.id,
  badgeStateKey: hub.badgeStateKey,
  fixed: hub.fixed
})

function pageEntry (router: Router, record: RouteRecordNormalized): NavCatalogEntry {
  const ancestry = getRouteTree(router).ancestry(record)
  const ancestorNames = ancestry.map(current => current.name)

  let category = 'general'
  let subcategory: string | undefined
  const reportsIndex = ancestorNames.indexOf('reports')
  if (reportsIndex > 0) {
    category = 'reports'
    // The record directly under Reports is the report category (or the page itself, e.g. My Saved Views).
    const reportCategory = ancestry[reportsIndex - 1]
    if (reportCategory !== record) {
      subcategory = reportCategory.meta?.label as string | undefined
    }
  } else if (ancestorNames.includes('settings')) {
    category = 'settings'
  } else {
    const hub = ancestorNames.map(name => HUB_BY_ROOT_ROUTE.get(String(name))).find(Boolean)
    if (hub) {
      category = hub.id
    }
  }

  // A page without its own icon wears its nearest ancestor's, then its area's.
  const icon = ancestry.map(current => current.meta?.navIcon).find(isNavIconName) ?? CATEGORY_ICONS[category] ?? FALLBACK_NAV_ICON
  const navColor = record.meta?.navColor
  const color = isNavColor(navColor) ? navColor : DEFAULT_NAV_COLOR

  return {
    id: String(record.name),
    kind: 'page',
    label: getRouteNavLabel(record),
    description: (record.meta?.description as string | undefined) || undefined,
    trail: getRoutePathString(router, record),
    icon,
    color,
    category,
    subcategory,
    searchAliases: (record.meta?.searchAliases as string[] | undefined) ?? [],
    routeNames: [String(record.name)]
  }
}

export interface NavCatalog {
  entries: NavCatalogEntry[]
  byId: Map<string, NavCatalogEntry>
}

const catalogs = new WeakMap<Router, NavCatalog>()

/** The whole catalog, regardless of who is looking. Filter with `resolveEntryRouteName` before showing it. */
export function getNavCatalog (router: Router): NavCatalog {
  const cached = catalogs.get(router)
  if (cached) {
    return cached
  }

  const tree = getRouteTree(router)
  const pages = getSearchableRouteRecords(router)
    .filter(record =>
      !EXCLUDED_ROUTE_NAMES.has(String(record.name)) &&
      tree.ancestry(record).some(current => current.name === ORG_ROOT_ROUTE_NAME)
    )
    .map(record => pageEntry(router, record))

  const entries = [...HUBS.map(hubEntry), ...pages]
  const catalog = { entries, byId: new Map(entries.map(entry => [entry.id, entry])) }
  catalogs.set(router, catalog)
  return catalog
}

/**
 * The picker's search: the same matching as the header search, over each entry's name, where it
 * sits, and any former names.
 */
export function searchNavCatalog (entries: NavCatalogEntry[], query: string): NavCatalogEntry[] {
  return matchSearchableRoutes(entries.map(entry => ({
    name: entry.id,
    searchAliases: entry.searchAliases,
    getRoutePathString: () => `${entry.label} / ${entry.trail}`,
    entry
  })), query).map(match => match.entry)
}

// Only the parts of the Vuex store the access checks read.
export interface NavAccessStore {
  state: { fatalError?: boolean, [key: string]: unknown }
  getters: Record<string, unknown>
}

/**
 * The route an entry should link to for the current user, or null if they may not open it.
 *
 * This is the one gate for both offering an entry in the picker and rendering a stored link, and
 * it is the same check the router guard applies on navigation, so the sidebar never shows a link
 * that would be refused. It reads live store state, so inside a computed it re-evaluates the
 * moment a permission or feature changes.
 */
export function resolveEntryRouteName (entry: NavCatalogEntry, router: Router, store: NavAccessStore): string | null {
  if (store.state.fatalError || store.getters.needsSetup) {
    return null
  }
  return entry.routeNames.find(name => hasAccessToRouteName(router, store, name)) ?? null
}

/**
 * How specifically an entry matches the current page, or -1 if it does not. The sidebar
 * highlights the most specific match, so on the time card editor a "Time Card Editor" link wins
 * over the Attendance hub when both are in the sidebar.
 */
export function entryMatchDepth (entry: NavCatalogEntry, route: RouteLocationNormalizedLoaded): number {
  if (entry.kind === 'hub') {
    const hub = HUB_BY_ID.get(entry.id)
    const inSection = getActiveSection(route) === entry.id ||
      (!!hub && route.matched.some(record => record.name === hub.rootRouteName))
    return inSection ? 0 : -1
  }
  const index = route.matched.findIndex(record => record.name === entry.id)
  return index === -1 ? -1 : index + 1
}
